/**
 * Filter model for the Game Pass → Configuration → Monthly landing page.
 *
 * The canonical filter-id enum (`MONTHLY_FILTER_ID` / `MonthlyFilterId`) lives
 * in `types/gamePass.ts` alongside `SEASONS_FILTER_ID` so the query layer
 * (`campaignsMappers`) can map a tab onto its `?status=` set without importing
 * from this component module. It's re-exported here so the page keeps a single
 * import site for everything it needs to render the filter strip.
 *
 * Card rows use the domain-neutral, presentational `SeasonData` model (and the
 * shared `SeasonCard`) so the monthly and seasons listings don't duplicate the
 * card contract — only the data *source* differs. Monthly's source is the
 * campaigns API, mapped by `campaignsMappers.partitionCampaignsByStatus`.
 */
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { MONTHLY_FILTER_ID } from "../../../../types/gamePass";
import type { MonthlyFilterId } from "../../../../types/gamePass";

export { MONTHLY_FILTER_ID };
export type { MonthlyFilterId };

export interface MonthlyFilterTab {
  id: MonthlyFilterId;
  label: string;
}

export const MONTHLY_FILTER_TABS: ReadonlyArray<MonthlyFilterTab> = [
  { id: MONTHLY_FILTER_ID.TIMELINE, label: AppStrings.SEASONS_FILTER_TIMELINE },
  { id: MONTHLY_FILTER_ID.ALL, label: AppStrings.SEASONS_FILTER_ALL },
  {
    id: MONTHLY_FILTER_ID.SCHEDULED,
    label: AppStrings.SEASONS_FILTER_SCHEDULED,
  },
  { id: MONTHLY_FILTER_ID.DRAFTS, label: AppStrings.SEASONS_FILTER_DRAFTS },
  { id: MONTHLY_FILTER_ID.COMPLETED, label: AppStrings.SEASONS_FILTER_COMPLETED },
  { id: MONTHLY_FILTER_ID.CANCELED, label: AppStrings.SEASONS_FILTER_CANCELED },
];

export const DEFAULT_MONTHLY_FILTER: MonthlyFilterId = MONTHLY_FILTER_ID.TIMELINE;

/** URL search-param name used to persist the active filter tab, so a refresh
 *  or a back-navigation restores the tab the operator was on. */
export const MONTHLY_FILTER_QUERY_PARAM = "filter";

/** Narrow an arbitrary string (URL search param, etc.) into a known
 *  `MonthlyFilterId`. Returns `null` for unknown values so callers can fall
 *  back to `DEFAULT_MONTHLY_FILTER`. */
export function parseMonthlyFilterId(
  value: string | null | undefined,
): MonthlyFilterId | null {
  if (!value) return null;
  const match = MONTHLY_FILTER_TABS.find((tab) => tab.id === value);
  return match ? match.id : null;
}
